import { onBeforeUnmount, readonly, ref, watch } from 'vue';
import ResizeObserver from 'resize-observer-polyfill';

import { IN_BROWSER, refElement } from '@components/utils';

import type { DeepReadonly, Ref } from 'vue';

interface IUseResizeObserver {
  resizeRef: Ref<HTMLElement | undefined>;
  contentRect: DeepReadonly<Ref<DOMRectReadOnly | undefined>>;
}

export function useResizeObserver(
  callback?: ResizeObserverCallback,
  box: 'content' | 'border' = 'content'
): IUseResizeObserver {
  const resizeRef = ref<HTMLElement | undefined>();
  const contentRect = ref<DOMRectReadOnly>();

  if (IN_BROWSER) {
    const observer = new ResizeObserver((entries: ResizeObserverEntry[]) => {
      callback?.(entries, observer);

      if (!entries.length) return;

      if (box === 'content') {
        contentRect.value = entries[0].contentRect;
      } else {
        contentRect.value = entries[0].target.getBoundingClientRect();
      }
    });

    onBeforeUnmount(() => {
      observer.disconnect();
    });

    watch(
      resizeRef,
      (newValue, oldValue) => {
        if (oldValue) {
          observer.unobserve(refElement(oldValue) as Element);
          contentRect.value = undefined;
        }

        if (newValue) {
          observer.observe(refElement(newValue) as Element);
        }
      },
      {
        flush: 'post'
      }
    );
  }

  return {
    resizeRef,
    contentRect: readonly(contentRect)
  };
}
